import {
  GetCampaignParams,
  Campaign,
  UseCalculatedFieldsReturn,
  useReturnOnAdSpend,
} from '@features/campaigns';
import {
  useCampaign,
  useReport,
  RslReportsParams,
  useCurrentAdSpend,
} from '@features/campaigns/subdomains/rsl';
import {
  useCostPerVisit,
  UseCostPerVisitOptions,
} from '@features/campaigns/subdomains/visitation/hooks';
import { getReportChannels } from '@features/campaigns/subdomains/rsl/hooks/getReportChannels';
import { BuildPresentationProps } from '../utils/build-presentation';
import {
  buildDimensionTableData,
  Lci3DimensionTableData,
  MAX_TOP_PERFORMERS,
  TOP_BOTTOM_NAMES,
} from '@features/lci-report';
import { sortBy } from '@utils/arrays';
import type {
  Lci3Report,
  Lci3SalesMetrics,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { ImageWithMetadata } from '@features/powerpoint';
import { CampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { useLatestCampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/hooks/useCampaignAnalytics';

type FetchParams = {
  reportParams: Omit<RslReportsParams, 'campaignId'>;
  campaignParams: GetCampaignParams;
};

type RslLciReportFieldsReturn = {
  loading: boolean;
  data?: {
    campaign: Campaign;
    selectedReportDate: string;
    brandLogo: ImageWithMetadata | null;
    salesSummary: Lci3Report['salesMetrics'] | null;
    visitMetrics: Lci3Report['visitMetrics']; // Required by shared form, but RSL doesn't track visits
    subdimensions: Lci3Report['subdimensions'];
    dimensionTableData: Lci3DimensionTableData;
    visitDimensionTableData: Lci3DimensionTableData;
    campaignAnalyticsReport: CampaignAnalyticsReport | null;
    calculatedFields: UseCalculatedFieldsReturn['data'];
    report: BuildPresentationProps['report'];
    hasVisitMetrics: boolean;
  };
  error?: string;
};

const buildSalesSummaryFromRslReport = (
  report: BuildPresentationProps['report']
): Lci3SalesMetrics => {
  const { combinedChannel, inStoreChannel, onlineChannel } =
    getReportChannels(report);

  return {
    incrementalSalesLift: {
      total: report.incrementalSalesLift,
      inStore: inStoreChannel?.incrementalSalesLift ?? null,
      online: onlineChannel?.incrementalSalesLift ?? null,
    },
    confidenceLevel: {
      total: report.incrementalSalesLiftConfidenceLevel,
      inStore: inStoreChannel?.incrementalSalesLiftConfidenceLevel ?? null,
      online: onlineChannel?.incrementalSalesLiftConfidenceLevel ?? null,
    },
    incrementalSales: {
      total: report.projectedIncrementalSales,
      inStore: inStoreChannel?.projectedIncrementalSales ?? null,
      online: onlineChannel?.projectedIncrementalSales ?? null,
    },
    totalProjectedSales: {
      total: report.totalProjectedSales,
      inStore: inStoreChannel?.totalProjectedSales ?? null,
      online: onlineChannel?.totalProjectedSales ?? null,
    },
    transactionSizeSales: {
      total: report.transactionSizeLift,
      inStore: inStoreChannel?.transactionSizeLift ?? null,
      online: onlineChannel?.transactionSizeLift ?? null,
    },
    transactionRateSales: {
      total: report.transactionRateLift,
      inStore: inStoreChannel?.transactionRateLift ?? null,
      online: onlineChannel?.transactionRateLift ?? null,
    },
    penetrationSales: {
      total: report.penetrationLift,
      inStore: inStoreChannel?.penetrationLift ?? null,
      online: onlineChannel?.penetrationLift ?? null,
    },
    exposedSalesRate: {
      total: report.salesRate ?? null,
      inStore: inStoreChannel?.salesRate ?? null,
      online: onlineChannel?.salesRate ?? null,
    },
    controlSalesRate: {
      total: null,
      inStore: null,
      online: null,
    },
    exposedTransactionSize: {
      total: report.transactionSize,
      inStore: inStoreChannel?.transactionSize ?? null,
      online: onlineChannel?.transactionSize ?? null,
    },
    controlTransactionSize: {
      total: report.controlTransactionSize,
      inStore: inStoreChannel?.controlTransactionSize ?? null,
      online: onlineChannel?.controlTransactionSize ?? null,
    },
    transactionSizeLift: {
      total: report.transactionSizeLift,
      inStore: inStoreChannel?.transactionSizeLift ?? null,
      online: onlineChannel?.transactionSizeLift ?? null,
    },
    exposedTransactionRate: {
      total: report.transactionRate,
      inStore: inStoreChannel?.transactionRate ?? null,
      online: onlineChannel?.transactionRate ?? null,
    },
    controlTransactionRate: {
      total: report.controlTransactionRate,
      inStore: inStoreChannel?.controlTransactionRate ?? null,
      online: onlineChannel?.controlTransactionRate ?? null,
    },
    transactionRateLift: {
      total: report.transactionRateLift,
      inStore: inStoreChannel?.transactionRateLift ?? null,
      online: onlineChannel?.transactionRateLift ?? null,
    },
    exposedPenetration: {
      total: report.penetration,
      inStore: inStoreChannel?.penetration ?? null,
      online: onlineChannel?.penetration ?? null,
    },
    controlPenetration: {
      total: report.controlPenetration,
      inStore: inStoreChannel?.controlPenetration ?? null,
      online: onlineChannel?.controlPenetration ?? null,
    },
    penetrationLift: {
      total: report.penetrationLift,
      inStore: inStoreChannel?.penetrationLift ?? null,
      online: onlineChannel?.penetrationLift ?? null,
    },
    incrementalSalesLiftPercentageIndex: {
      total: null,
      inStore: null,
      online: null,
    },
    totalSalesPerImpressionIndex: {
      total:
        combinedChannel?.projectedActualTransactionValuePerImpression ?? null,
      inStore:
        inStoreChannel?.projectedActualTransactionValuePerImpression ?? null,
      online:
        onlineChannel?.projectedActualTransactionValuePerImpression ?? null,
    },
    incrementalSalesPerImpressionIndex: {
      total:
        combinedChannel?.projectedIncrementalTransactionValuePerImpression ??
        null,
      inStore:
        inStoreChannel?.projectedIncrementalTransactionValuePerImpression ??
        null,
      online:
        onlineChannel?.projectedIncrementalTransactionValuePerImpression ??
        null,
    },
  };
};

const buildDimensionTableDataFromRslDimensions = (
  dimensions: BuildPresentationProps['report']['dimensions']
): Lci3DimensionTableData => {
  const validDimensions = dimensions.filter(d => d.salesLiftIndex !== null);

  const sortedDimensions = [...validDimensions].sort(
    sortBy('salesLiftIndex', 'desc')
  );

  const totalDimensions = sortedDimensions.length;
  const topCount = Math.min(MAX_TOP_PERFORMERS, totalDimensions);
  const bottomCount = Math.min(MAX_TOP_PERFORMERS, totalDimensions);

  const dimensionTableData: Lci3DimensionTableData = {
    Top: [],
    Bottom: [],
  };

  sortedDimensions.forEach((dimension, index) => {
    const subdimension: Lci3Report['subdimensions'][number] = {
      reportId: dimension.rslReportId,
      name: dimension.dimensionName,
      value: dimension.dimensionValue,
      rawValue: dimension.dimensionValue,
      statisticallySignificant: dimension.statisticallySignificant,
      visitMetrics: {
        optimizationIndex: dimension.incrementalVisitLiftIndex ?? null,
        confidenceLevel: dimension.incrementalVisitLiftConfidenceLevel ?? null,
        impressions: dimension.impressions,
        uniqueUsers: dimension.uniqueUsers ?? 0,
        projectedVisits: dimension.totalProjectedVisits ?? null,
        incrementalVisits: dimension.projectedIncrementalVisits ?? null,
        incrementalLift: dimension.incrementalVisitLift ?? null,
        incrementalVisitLiftPercentageIndex:
          dimension.incrementalVisitLiftIndex ?? null,
        totalVisitsPerImpressionIndex:
          dimension.totalVisitsPerImpressionIndex ?? null,
        incrementalVisitsPerImpressionIndex:
          dimension.incrementalVisitsPerImpressionIndex ?? null,
      },
      salesMetrics: {
        incrementalSalesLift: {
          total: dimension.incrementalSalesLift,
          inStore: null,
          online: null,
        },
        confidenceLevel: {
          total: dimension.liftConfidenceLevel,
          inStore: null,
          online: null,
        },
        incrementalSales: {
          total: dimension.projectedIncrementalSales,
          inStore: null,
          online: null,
        },
        totalProjectedSales: {
          total: dimension.totalProjectedSales,
          inStore: null,
          online: null,
        },
        transactionSizeSales: {
          total: dimension.transactionSizeLift,
          inStore: null,
          online: null,
        },
        transactionRateSales: {
          total: dimension.transactionRateLift,
          inStore: null,
          online: null,
        },
        penetrationSales: {
          total: dimension.penetrationLift,
          inStore: null,
          online: null,
        },
        exposedSalesRate: {
          total: dimension.salesRate ?? null,
          inStore: null,
          online: null,
        },
        controlSalesRate: { total: null, inStore: null, online: null },
        exposedTransactionSize: {
          total: dimension.transactionSize,
          inStore: null,
          online: null,
        },
        controlTransactionSize: {
          total: dimension.controlTransactionSize,
          inStore: null,
          online: null,
        },
        transactionSizeLift: {
          total: dimension.transactionSizeLift,
          inStore: null,
          online: null,
        },
        exposedTransactionRate: {
          total: dimension.transactionRate,
          inStore: null,
          online: null,
        },
        controlTransactionRate: {
          total: dimension.controlTransactionRate,
          inStore: null,
          online: null,
        },
        transactionRateLift: {
          total: dimension.transactionRateLift,
          inStore: null,
          online: null,
        },
        exposedPenetration: {
          total: dimension.penetration,
          inStore: null,
          online: null,
        },
        controlPenetration: {
          total: dimension.controlPenetration,
          inStore: null,
          online: null,
        },
        penetrationLift: {
          total: dimension.penetrationLift,
          inStore: null,
          online: null,
        },
        incrementalSalesLiftPercentageIndex: {
          total: null,
          inStore: null,
          online: null,
        },
        totalSalesPerImpressionIndex: {
          total: null,
          inStore: null,
          online: null,
        },
        incrementalSalesPerImpressionIndex: {
          total: null,
          inStore: null,
          online: null,
        },
      },
    };

    // Add to dimension name group
    dimensionTableData[dimension.dimensionName] ??= [];
    dimensionTableData[dimension.dimensionName]?.push(subdimension);

    // Add to Top or Bottom
    if (index < topCount) {
      dimensionTableData.Top?.push(subdimension);
    } else if (index >= totalDimensions - bottomCount) {
      dimensionTableData.Bottom?.push(subdimension);
    }
  });

  dimensionTableData.Bottom = (dimensionTableData.Bottom ?? []).toReversed();

  return dimensionTableData;
};

export const useRslLciReportFields = (
  fetchParams: FetchParams
): RslLciReportFieldsReturn => {
  const { campaign, isFetching: loadingCampaign } = useCampaign(
    fetchParams.campaignParams
  );

  const { report, loading: loadingReport } = useReport({
    campaignId: campaign?.id,
    campaignParams: fetchParams.campaignParams,
    reportParams: fetchParams.reportParams,
  });

  const {
    report: campaignAnalyticsReport = null,
    isLoading: loadingCampaignAnalyticsReport,
  } = useLatestCampaignAnalyticsReport(fetchParams.campaignParams.id);

  const salesSummary = report ? buildSalesSummaryFromRslReport(report) : null;

  const adSpendHook = useCurrentAdSpend();

  const costPerVisitReport: UseCostPerVisitOptions['report'] = report
    ? {
        reportDate: report.reportDate,
        visitMetrics: {
          projectedVisits: report.totalProjectedVisits ?? null,
          incrementalVisits: report.projectedIncrementalVisits ?? null,
        },
      }
    : null;

  const campaignForCpv: UseCostPerVisitOptions['campaign'] = campaign ?? {
    id: fetchParams.campaignParams.id,
    startDate: '',
    endDate: '',
  };

  const { value: costPerVisit, isFetching: loadingCpv } = useCostPerVisit({
    campaign: campaignForCpv,
    report: costPerVisitReport,
    visitsField: 'projectedVisits',
    adSpend: adSpendHook.adSpend ?? 0,
  });

  const { value: costPerIncrementalVisit, isFetching: loadingCpiv } =
    useCostPerVisit({
      campaign: campaignForCpv,
      report: costPerVisitReport,
      visitsField: 'incrementalVisits',
      adSpend: adSpendHook.adSpend ?? 0,
    });

  const { value: iroas, isFetching: loadingIroas } = useReturnOnAdSpend({
    campaign: campaignForCpv,
    reportDate: report?.reportDate ?? null,
    sales: salesSummary?.incrementalSales.total ?? null,
    adSpend: adSpendHook.adSpend,
  });

  const calculatedFields: UseCalculatedFieldsReturn['data'] = {
    adSpend: campaign?.adSpend ?? null,
    costPerVisit,
    costPerIncrementalVisit,
    returnOnAdSpend: null,
    incrementalReturnOnAdSpend: iroas,
  };

  const loading =
    loadingCampaign ||
    loadingReport ||
    loadingCampaignAnalyticsReport ||
    loadingCpv ||
    loadingCpiv ||
    loadingIroas;

  if (!campaign || !report) {
    return { error: 'Missing required data for PPT generation', loading };
  }

  const dimensionTableData = buildDimensionTableDataFromRslDimensions(
    report.dimensions
  );

  const allDimensionNames = Object.keys(dimensionTableData).filter(
    key => !TOP_BOTTOM_NAMES.includes(key as (typeof TOP_BOTTOM_NAMES)[number])
  );
  const subdimensions = allDimensionNames.flatMap(
    name => dimensionTableData[name] ?? []
  );

  const visitDimensionTableData = buildDimensionTableData(subdimensions);

  return {
    loading,
    data: {
      campaign,
      selectedReportDate: report.reportDate,
      brandLogo: null,
      salesSummary,
      visitMetrics: {
        impressions: report.impressions,
        uniqueUsers: report.uniqueUsers ?? 0,
        locationsMeasured: 0,
        incrementalVisits: report.projectedIncrementalVisits ?? null,
        incrementalLift: report.incrementalVisitLift ?? null,
        confidenceLevel: report.incrementalVisitLiftConfidenceLevel ?? null,
        matchedForAttribution: report.matchedForAttribution ?? null,
        projectedVisits: report.totalProjectedVisits ?? null,
        costPerVisit,
        costPerIncrementalVisit,
        visitCountCumulative: 0,
      },
      subdimensions,
      dimensionTableData,
      visitDimensionTableData,
      campaignAnalyticsReport,
      calculatedFields,
      report,
      hasVisitMetrics: report.hasVisitMetrics,
    },
  };
};
